import {useCallback, useMemo} from 'react';
import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {
  selectSitePlan,
  selectWorkspaceSlug,
} from '@packages/systems/designer/site-data';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import webflowFetch from '@packages/utilities/fetch';
import type {WorkspaceMember} from '@packages/shared/types/dashboard/billing';

type WorkspaceMemberResult = {
  members: WorkspaceMember[];
};

const useWorkspaceMembers = (
  enabled: boolean
): UseQueryResult<WorkspaceMemberResult> => {
  const workspaceSlug = useDesignerSelector(selectWorkspaceSlug) ?? '';

  const getWorkspaceMembers =
    useCallback(async (): Promise<WorkspaceMemberResult> => {
      const response = await webflowFetch(
        `/api/workspaces/${workspaceSlug}/members`
      );
      if (!response.ok) {
        throw new Error(
          `Fetching workspace members error: ${response.statusText}`
        );
      }
      return response.json();
    }, [workspaceSlug]);

  return useQuery<WorkspaceMemberResult>({
    queryKey: ['workspace-members', workspaceSlug],
    queryFn: getWorkspaceMembers,
    enabled: enabled && Boolean(workspaceSlug),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

type EmailDestinationRestriction = {
  isRestricted: boolean;
  allowedEmailDestinations: Set<string> | null;
  isLoading: boolean;
};

/**
 * Hook that determines if email destinations are restricted to workspace members
 * and provides the set of allowed workspace member emails.
 * @returns An object containing the restriction status, the set of allowed workspace member emails, and the loading state.
 */
export const useEmailDestinationRestriction =
  (): EmailDestinationRestriction => {
    const sitePlan = useDesignerSelector(selectSitePlan);
    const isFreePlan = sitePlan === 'free';

    const isWorkspaceRestrictionEnabled = useFeatureFlag(
      FEATURE_FLAGS.FREE_WORKSPACE_EMAIL_DESTINATION_RESTRICTION
    );

    const isRestricted = isFreePlan && isWorkspaceRestrictionEnabled;

    const {data, isLoading} = useWorkspaceMembers(isRestricted);

    const allowedEmailDestinations = useMemo(() => {
      if (data?.members == null) {
        return null;
      }
      return new Set(data.members.map((member) => member.email));
    }, [data?.members]);

    return {
      isRestricted,
      allowedEmailDestinations,
      isLoading: isRestricted && isLoading,
    };
  };
